import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaAndroid, FaApple, FaReact, FaGamepad, FaShoppingCart, FaCloud, FaCheckCircle, FaChevronDown, FaChevronUp, FaCode, FaLightbulb, FaPencilRuler, FaLaptopCode, FaRocket, FaBug, FaHeadset } from 'react-icons/fa';
import { SiFlutter, SiKotlin, SiSwift, SiFirebase, SiNodedotjs } from 'react-icons/si';
import heroImage from '../../assets/d19a8f50f70944b0c4b2fdf4553d2462.jpg';

const AppDevelopmentPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaAndroid, title: 'Android App Development', desc: 'Native Android apps using Kotlin/Java with Material Design standards', image: 'https://images.unsplash.com/photo-1607252650355-f7fd0460ccdb?w=600' },
        { icon: FaApple, title: 'iOS App Development', desc: 'Premium iOS applications built with Swift and SwiftUI for Apple devices', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600' },
        { icon: FaReact, title: 'Cross-Platform Apps', desc: 'One codebase for both Android & iOS using React Native or Flutter', image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600' },
        { icon: FaGamepad, title: 'Mobile Game Development', desc: 'Immersive 2D & 3D mobile games using Unity and Unreal Engine', image: 'https://images.unsplash.com/photo-1556438050-bf886e5e261d?w=600' },
        { icon: FaShoppingCart, title: 'M-Commerce Solutions', desc: 'Mobile shopping apps with secure payment gateways and tracking', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600' },
        { icon: FaCloud, title: 'Enterprise Mobility', desc: 'Secure mobile solutions for business process automation', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600' }
    ];

    const technologies = [
        { name: 'Flutter', icon: SiFlutter, desc: 'Google\'s UI toolkit for natively compiled apps' },
        { name: 'React Native', icon: FaReact, desc: 'Build mobile apps using JavaScript and React' },
        { name: 'Kotlin', icon: SiKotlin, desc: 'Modern programming language for Android' },
        { name: 'Swift', icon: SiSwift, desc: 'Powerful language for iOS development' },
        { name: 'Firebase', icon: SiFirebase, desc: 'Backend-as-a-Service for mobile apps' },
        { name: 'Node.js', icon: SiNodedotjs, desc: 'Scalable backend API services' },
        { name: 'Material UI', icon: FaCode, desc: 'Android design system implementation' },
        { name: 'Cloud API', icon: FaCloud, desc: 'AWS/Azure cloud integration services' }
    ];

    const process = [
        { step: '01', title: 'Strategy & Concept', desc: 'Defining the app idea, target audience, and core features' },
        { step: '02', title: 'UI/UX Design', desc: 'Creating intuitive wireframes and beautiful user interfaces' },
        { step: '03', title: 'Development', desc: 'Coding the application with clean, scalable architecture' },
        { step: '04', title: 'Testing & QA', desc: 'Rigorous testing on multiple devices to ensure bug-free performance' },
        { step: '05', title: 'App Store Launch', desc: 'End-to-end management of Play Store and App Store submission' },
        { step: '06', title: 'Maintenance', desc: 'Regular updates, performance monitoring, and support' }
    ];

    // New Colorful Steps for Horizontal Flow
    const flowSteps = [
        { title: 'Idea', icon: FaLightbulb, color: 'from-yellow-400 to-orange-500' },
        { title: 'Design', icon: FaPencilRuler, color: 'from-pink-500 to-rose-500' },
        { title: 'Code', icon: FaLaptopCode, color: 'from-blue-500 to-cyan-500' },
        { title: 'Test', icon: FaBug, color: 'from-purple-500 to-indigo-500' },
        { title: 'Launch', icon: FaRocket, color: 'from-green-400 to-emerald-600' },
        { title: 'Support', icon: FaHeadset, color: 'from-orange-400 to-red-500' }
    ];

    const faqs = [
        { q: 'Do you build both Android and iOS apps?', a: 'Yes, we specialize in both native development (Kotlin/Swift) and cross-platform development (Flutter/React Native) to cover all devices.' },
        { q: 'How much does it cost to build an app?', a: 'Basic apps start from ₹50,000, while complex enterprise apps vary based on features. Contact us using the form for a precise quote.' },
        { q: 'How long does it take to develop an app?', a: 'A standard app typically takes 4-12 weeks depending on complexity. We follow an agile process to ensure timely delivery.' },
        { q: 'Will you help upload the app to Play Store/App Store?', a: 'Absolutely! We handle the entire deployment process, including creating store listings, screenshots, and meeting compliance guidelines.' },
        { q: 'Do you provide support after the app is live?', a: 'Yes, we offer 6 months of free technical support and maintenance packages to keep your app compatible with new OS versions.' }
    ];

    const whyChooseUs = [
        { title: 'User-Centric Design', desc: 'We prioritize UI/UX to ensure high user engagement and retention rates.' },
        { title: 'Cross-Platform Expertise', desc: 'Save costs with high-performance apps that run on both Android & iOS.' },
        { title: 'Scalable Architecture', desc: 'Apps built to handle millions of users without performance degradation.' },
        { title: 'Security First', desc: 'Advanced encryption and security protocols to protect user data.' },
        { title: 'Agile Development', desc: 'Rapid iteration cycles with regular updates and client feedback.' },
        { title: 'Post-Launch Success', desc: 'Analytics integration and ASO strategies to help your app grow.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-bottom"
                    style={{ backgroundImage: `url(${heroImage})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-purple-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-blue-300 mb-3">What We Do &gt; Digital Business</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Android & iOS Development</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Transforming ideas into high-performance mobile applications
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-blue-600 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Free Consultation
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-blue-600 transition-all text-sm whitespace-nowrap">
                                Explore Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* How We Build Apps (RESTORED PROCESS GRID) */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">App Development Lifecycle</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            From concept to launch, we follow a streamlined process to deliver world-class mobile applications.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 mt-16">
                        {process.slice(0, 3).map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-6xl font-bold text-blue-200 mb-4">{item.step}</div>
                                <h3 className="text-xl font-medium text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 mt-8">
                        {process.slice(3).map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-6xl font-bold text-blue-200 mb-4">{item.step}</div>
                                <h3 className="text-xl font-medium text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Mobile Technology Stack</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            We use the latest frameworks and tools to build future-proof mobile applications.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {technologies.map((tech, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <tech.icon className="text-5xl mx-auto mb-4 text-blue-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{tech.name}</h3>
                                <p className="text-sm text-slate-600">{tech.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (HCL Style) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">What We Build</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Tailored mobile solutions for every business need and industry vertical.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-purple-600 to-indigo-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why Partner With Us?</h2>
                        <p className="text-lg text-purple-100 max-w-3xl mx-auto">
                            We don't just build apps; we create digital experiences that deliver results.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-green-400 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-purple-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NEW: Horizontal Colorful Steps (Above FAQ) - FIXED COLORS */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Our Development Timeline</h2>
                        <p className="text-slate-600">Your journey from idea to live application</p>
                    </div>

                    <div className="relative">
                        {/* Connecting Line (Horizontal on Desktop) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 -z-0"></div>

                        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
                            {flowSteps.map((step, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="relative flex flex-col items-center text-center z-10 group"
                                >
                                    {/* Circle Icon */}
                                    <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white`}>
                                        <step.icon />
                                    </div>

                                    {/* Step Number */}
                                    <div className="absolute top-0 right-1/2 translate-x-12 -translate-y-2 w-6 h-6 rounded-full bg-white text-slate-900 text-xs font-bold flex items-center justify-center border-2 border-slate-200 shadow-sm">
                                        {idx + 1}
                                    </div>

                                    {/* Label */}
                                    <h3 className="text-lg font-medium text-slate-900 group-hover:text-purple-600 transition-colors">{step.title}</h3>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form Section */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs - Left Side */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Frequently Asked Questions</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-purple-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form - Right Side */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Get a Free Quote</h2>
                            <p className="text-slate-600 mb-6">Fill out the form below to kickstart your mobile app project.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                                    <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="John Doe" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                        <input type="email" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                        <input type="tel" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="+91 98765 43210" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Project Type</label>
                                    <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600">
                                        <option>Android App Development</option>
                                        <option>iOS App Development</option>
                                        <option>Cross-Platform App</option>
                                        <option>App Maintenance</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                                    <textarea rows="4" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="Tell us about your app idea..."></textarea>
                                </div>
                                <button type="submit" className="w-full bg-purple-600 text-white font-medium py-3 rounded-sm hover:bg-purple-700 transition-colors shadow-lg">
                                    Send Inquiry
                                </button>
                            </form>
                        </div>

                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Have an Amazing App Idea?</h2>
                    <p className="text-xl font-light mb-8 text-purple-100">
                        Let's build the next big thing together
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-purple-600 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Contact Us Today
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-purple-600 transition-all text-sm whitespace-nowrap">
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AppDevelopmentPage;
